import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../utils/AuthContext';
import { useToast, ChakraProvider } from '@chakra-ui/react';

const EXTENSION_ID = process.env.REACT_APP_EXTENSION_ID;
const EXTENSION_INSTALL_URL =
  'https://chromewebstore.google.com/detail/tspl-evaluation-screen-recorder/einbacniddmglnphaaibidanemaheegb';

const StartExam = () => {
  const { auth } = useAuth();

  const [assignedPaperSets, setAssignedPaperSets] = useState([]);
  const [selectedPaperSetId, setSelectedPaperSetId] = useState('');
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [timer, setTimer] = useState(30 * 60);
  const [error, setError] = useState(null);

  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [screenReady, setScreenReady] = useState(false);

  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraTimerRef = useRef(null);

  const screenStreamRef = useRef(null);
  const screenTimerRef = useRef(null);

  const toast = useToast();
  const navigate = useNavigate();

  const getUsernameForUpload = () =>
    auth?.username || auth?.userInfo?.email || auth?.userId || 'unknown';

  const checkExtension = useCallback(() => {
    return new Promise((resolve) => {
      if (!EXTENSION_ID || !window.chrome?.runtime?.sendMessage) {
        resolve(false);
        return;
      }

      window.chrome.runtime.sendMessage(
        EXTENSION_ID,
        { type: 'PING' },
        (response) => {
          if (window.chrome.runtime.lastError) {
            console.error('Extension ping error:', window.chrome.runtime.lastError.message);
            resolve(false);
            return;
          }

          resolve(!!response?.installed);
        }
      );
    });
  }, []);

  const authorizeExtension = useCallback((paperSetId) => {
    return new Promise((resolve, reject) => {
      window.chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'AUTH',
          token: auth.token,
          examSessionId: Number(paperSetId)
        },
        (response) => {
          if (window.chrome.runtime.lastError) {
            reject(window.chrome.runtime.lastError.message);
            return;
          }

          if (!response?.ok) {
            reject(response?.error || 'Extension authorization failed');
            return;
          }

          resolve(response);
        }
      );
    });
  }, [auth.token]);

  const uploadBlobToOldApi = async (blob, capturetype, filePrefix) => {
    if (!blob) return;

    const formData = new FormData();
    formData.append('Image', blob, `${filePrefix}_${Date.now()}.jpg`);
    formData.append('UserId', String(auth.userId));
    formData.append('username', String(getUsernameForUpload()));
    formData.append('capturetype', capturetype);

    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/Exam/upload-screenshot`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${auth.token}`
        }
      }
    );
  };

  const captureFrameBlob = async (stream, widthFallback = 1280, heightFallback = 720) => {
    if (!stream) return null;

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    await video.play();

    await new Promise((resolve) => {
      if (video.videoWidth > 0 && video.videoHeight > 0) resolve();
      else video.onloadedmetadata = resolve;
    });

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || widthFallback;
    canvas.height = video.videoHeight || heightFallback;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.75);
    });
  };

  const uploadScreenShotFromStream = async (stream) => {
    try {
      const blob = await captureFrameBlob(stream, 1280, 720);
      await uploadBlobToOldApi(blob, 'screen', 'screen');
    } catch (err) {
      console.error('Screen screenshot upload failed:', err);
    }
  };

  const uploadCameraShotFromStream = async (stream) => {
    try {
      const blob = await captureFrameBlob(stream, 640, 480);
      await uploadBlobToOldApi(blob, 'camera', 'camera');
    } catch (err) {
      console.error('Camera screenshot upload failed:', err);
    }
  };

  const attachCameraPreview = useCallback(async () => {
    if (!cameraStreamRef.current || !cameraVideoRef.current) return;

    cameraVideoRef.current.srcObject = cameraStreamRef.current;
    cameraVideoRef.current.muted = true;
    cameraVideoRef.current.playsInline = true;

    try {
      await cameraVideoRef.current.play();
    } catch (err) {
      console.error('Camera preview play failed:', err);
    }
  }, []);

  const startScreenCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false
      });

      const track = stream.getVideoTracks()[0];
      const settings = track?.getSettings?.() || {};

      if (settings.displaySurface && settings.displaySurface !== 'monitor') {
        stream.getTracks().forEach(t => t.stop());

        toast({
          title: 'Entire Screen Required',
          description: 'Please select Entire Screen only. Do not select Window or Browser Tab.',
          status: 'error',
          duration: 7000,
          isClosable: true,
        });

        return false;
      }

      screenStreamRef.current = stream;
      setScreenReady(true);

      await uploadScreenShotFromStream(stream);

      screenTimerRef.current = setInterval(() => {
        uploadScreenShotFromStream(stream);
      }, 20000);

      if (track) {
        track.addEventListener('ended', () => {
          stopScreenCapture();

          toast({
            title: 'Screen sharing stopped',
            description: 'Exam monitoring has stopped. Please contact admin.',
            status: 'error',
            duration: 7000,
            isClosable: true,
          });
        });
      }

      return true;
    } catch (err) {
      console.error('Screen capture error:', err);

      setScreenReady(false);

      toast({
        title: 'Screen Permission Required',
        description: 'Please select Entire Screen and click Share.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      return false;
    }
  };

  const stopScreenCapture = () => {
    if (screenTimerRef.current) {
      clearInterval(screenTimerRef.current);
      screenTimerRef.current = null;
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    setScreenReady(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false
      });

      cameraStreamRef.current = stream;
      setCameraReady(true);

      setTimeout(() => {
        attachCameraPreview();
      }, 100);

      await uploadCameraShotFromStream(stream);

      cameraTimerRef.current = setInterval(() => {
        uploadCameraShotFromStream(stream);
      }, 20000);

      const track = stream.getVideoTracks()[0];
      if (track) {
        track.addEventListener('ended', () => {
          stopCamera();

          toast({
            title: 'Camera stopped',
            description: 'Camera monitoring has stopped. Please contact admin.',
            status: 'error',
            duration: 7000,
            isClosable: true,
          });
        });
      }

      return true;
    } catch (err) {
      console.error('Camera error:', err);

      setCameraReady(false);

      toast({
        title: 'Camera Permission Required',
        description: 'Please allow camera access to start exam.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      return false;
    }
  };

  const stopCamera = () => {
    if (cameraTimerRef.current) {
      clearInterval(cameraTimerRef.current);
      cameraTimerRef.current = null;
    }

    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }

    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }

    setCameraReady(false);
  };

  useEffect(() => {
    const fetchAssignedPaperSets = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/Exam/assignments/${auth.userId}`,
          {
            headers: {
              Authorization: `Bearer ${auth.token}`,
            },
          }
        );

        setAssignedPaperSets(response.data.$values || response.data);
      } catch (err) {
        setError('Error fetching assigned paper sets');
        console.error(err);
      }
    };

    fetchAssignedPaperSets();
  }, [auth.token, auth.userId]);

  useEffect(() => {
    const initExtensionCheck = async () => {
      const installed = await checkExtension();
      setExtensionInstalled(installed);
    };

    initExtensionCheck();
  }, [checkExtension]);

  useEffect(() => {
    const handleFocus = async () => {
      const installed = await checkExtension();
      setExtensionInstalled(installed);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkExtension]);

  useEffect(() => {
    if (examStarted && cameraReady) {
      attachCameraPreview();
    }
  }, [examStarted, cameraReady, attachCameraPreview]);

  const fetchQuestions = useCallback(async (paperSetId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/PaperSet/${paperSetId}/questions`,
      {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      }
    );

    setQuestions(response.data.$values || response.data);
  }, [auth.token]);

  useEffect(() => {
    if (examStarted && questions.length > 0) {
      setTimer(30 * 60);

      const countdown = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer === 0) {
            clearInterval(countdown);
            handleSubmit();
            return 0;
          }

          return prevTimer - 1;
        });
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [examStarted, questions.length]);

  useEffect(() => {
    return () => {
      stopScreenCapture();
      stopCamera();
    };
  }, []);

  const handlePaperSetChange = (e) => {
    const newPaperSetId = e.target.value;

    const selectedPaperSet = assignedPaperSets.find(
      ps => ps.paperSetId === parseInt(newPaperSetId, 10)
    );

    if (selectedPaperSet?.isCompleted) {
      toast({
        title: 'Exam Completed',
        description: 'This exam has already been completed.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });

      setSelectedPaperSetId('');
      setQuestions([]);
      setResponses({});
      setExamStarted(false);
      return;
    }

    setSelectedPaperSetId(newPaperSetId);
    setQuestions([]);
    setResponses({});
    setExamStarted(false);
  };

  const handleStartExam = async () => {
    if (!selectedPaperSetId) {
      toast({
        title: 'Select Paper Set',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const installed = await checkExtension();
    setExtensionInstalled(installed);

    if (!installed) {
      toast({
        title: 'Recorder Extension Required',
        description: 'Please install TSPL Evaluation Recorder before starting exam.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
      return;
    }

    try {
      await authorizeExtension(selectedPaperSetId);

      const screenOk = await startScreenCapture();
      if (!screenOk) return;

      const cameraOk = await startCamera();
      if (!cameraOk) {
        stopScreenCapture();
        return;
      }

      await fetchQuestions(selectedPaperSetId);
      setExamStarted(true);

      toast({
        title: 'Exam Started',
        description: 'Screen and camera capture are active.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      stopScreenCapture();
      stopCamera();

      toast({
        title: 'Cannot Start Exam',
        description: String(err),
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleChange = (questionId, optionId) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [questionId]: optionId,
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const responseSheet = Object.keys(responses).map((questionId) => ({
      userId: auth.userId,
      paperSetId: Number(selectedPaperSetId),
      questionId: parseInt(questionId, 10),
      selectedOptionId: responses[questionId],
    }));

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/Exam/responses`,
        responseSheet,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          },
        }
      );

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/Results/processExamResponses`,
        {
          userId: auth.userId,
          paperSetId: Number(selectedPaperSetId),
          responseDtos: responseSheet
        },
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      stopScreenCapture();
      stopCamera();

      toast({
        title: 'Exam submitted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/user');
    } catch (err) {
      toast({
        title: 'Error submitting exam',
        description: err.response?.data || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const recheckExtension = async () => {
    const installed = await checkExtension();
    setExtensionInstalled(installed);

    toast({
      title: installed ? 'Recorder Installed' : 'Recorder Not Found',
      status: installed ? 'success' : 'error',
      duration: 3000,
      isClosable: true,
    });
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="max-w-4xl mx-auto p-8 bg-white shadow-md rounded-lg">
      <h2 className="text-3xl font-bold mb-6 text-center">Online Exam</h2>

      <div className={`mb-4 p-3 rounded text-center ${extensionInstalled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {extensionInstalled ? (
          <b>TSPL Recorder Extension Installed ✓</b>
        ) : (
          <>
            <b>TSPL Recorder Extension Missing</b>
            <br />

            {EXTENSION_INSTALL_URL && (
              <button
                type="button"
                onClick={() => window.open(EXTENSION_INSTALL_URL, '_blank')}
                className="mt-2 bg-blue-500 text-white px-4 py-2 rounded"
              >
                Install Extension
              </button>
            )}

            <button
              type="button"
              onClick={recheckExtension}
              className="mt-2 ml-2 bg-gray-500 text-white px-4 py-2 rounded"
            >
              Recheck
            </button>
          </>
        )}
      </div>

      {!examStarted && (
        <div className="mb-4 p-3 rounded bg-yellow-100 text-yellow-800 text-sm">
          <b>Important:</b> When screen sharing starts, select <b>Entire Screen</b> only.
          Do not select Window or Browser Tab.
        </div>
      )}

      <div className="mb-4">
        <video
          ref={cameraVideoRef}
          autoPlay
          muted
          playsInline
          width="250"
          height="180"
          style={{
            border: cameraReady ? '2px solid green' : '2px solid #999',
            background: '#000',
            borderRadius: 8,
            display: examStarted || cameraReady ? 'block' : 'none'
          }}
        />
      </div>

      {examStarted && (
        <>
          <div className="mb-4 text-center">
            <span className="font-bold">Time Remaining: </span>
            <span>{formatTime(timer)}</span>
          </div>

          <div className="mb-4 text-center">
            <span className={screenReady ? 'text-green-600' : 'text-red-600'}>
              Screen: {screenReady ? 'Entire Screen Active' : 'Not Active'}
            </span>
            {' | '}
            <span className={cameraReady ? 'text-green-600' : 'text-red-600'}>
              Camera: {cameraReady ? 'Active' : 'Not Active'}
            </span>
          </div>
        </>
      )}

      {assignedPaperSets.length === 0 ? (
        <div className="text-red-500 text-center">No paper sets assigned to you.</div>
      ) : (
        <>
          {!examStarted && (
            <>
              <div className="mb-4">
                <label className="block mb-2 font-semibold">Select Paper Set:</label>
                <select
                  value={selectedPaperSetId}
                  onChange={handlePaperSetChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select</option>
                  {assignedPaperSets.map((paperSet) => (
                    <option key={paperSet.examAssignmentId} value={paperSet.paperSetId}>
                      {paperSet.paperSetTitle}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleStartExam}
                disabled={!extensionInstalled || !selectedPaperSetId}
                className={`w-full text-white px-4 py-2 rounded mt-4 ${
                  extensionInstalled && selectedPaperSetId
                    ? 'bg-blue-500'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                Start Exam
              </button>
            </>
          )}

          {examStarted && (
            <form onSubmit={handleSubmit}>
              {questions.map((question, index) => (
                <div key={question.questionId} className="mb-6">
                  <p className="font-medium mb-2">
                    {index + 1}. {question.questionText}
                  </p>

                  <div>
                    {(question.options?.$values || question.options || []).map((option) => (
                      <div key={option.optionId} className="mb-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`question-${question.questionId}`}
                            value={option.optionId}
                            checked={responses[question.questionId] === option.optionId}
                            onChange={() => handleChange(question.questionId, option.optionId)}
                            className="mr-2"
                          />
                          {option.optionText}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <button
                type="submit"
                className="w-full bg-blue-500 text-white px-4 py-2 rounded mt-4"
              >
                Submit Exam
              </button>
            </form>
          )}
        </>
      )}

      {error && (
        <div className="text-red-500 mt-4 text-center">
          {error}
        </div>
      )}
    </div>
  );
};

const App = () => (
  <ChakraProvider>
    <StartExam />
  </ChakraProvider>
);

export default App;